import {
  parseCustomerAddressForBackfill,
} from "@/modules/customers/domain/address";
import type { OrderDetailDTO } from "@/modules/orders/domain/types";
import type { ShopSettingsDTO } from "./contracts";

export type ShippingLabelViewModel = {
  senderPhone: string;
  recipientName: string;
  recipientPhone: string;
  categoryLabel: string;
  /** Full line for compact display (e.g. dialog subtitle, text fallback). */
  carrierLine: string;
  carrierName: string;
  branchName: string | null;
  /** ຈຸດສັງเกต / ບ້ານເລກ — ບໍ່ລວມບ້ານ (ບ) */
  addressDetail: string | null;
  /** ເມືອງ + ແຂວງ — ບໍ່ລວມບ້ານ */
  localityLine: string | null;
  /** ບັນທັດສຳລັບ text mode / fallback (detail + locality, ຄັ່ນ newline) */
  address: string;
  isCod: boolean;
  codAmount: string;
  collectShippingAtDestination: boolean;
};

function cleanPart(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}

/** ປ້າຍສົ່ງ: ບໍ່ສະແດງບ້ານ (ບ) — ສາຂາຢູ່ເທິງເມືອງ/ແຂວງໃນ UI */
export function formatShippingLabelAddressParts(
  customer: OrderDetailDTO["customer"],
): { addressDetail: string | null; localityLine: string | null } {
  const addressLine = cleanPart(customer.addressLine);
  const district = cleanPart(customer.district);
  const province = cleanPart(customer.province);
  const hasStructured = Boolean(
    addressLine || district || province || cleanPart(customer.village),
  );

  if (hasStructured) {
    const localityChunks: string[] = [];
    if (district) localityChunks.push(`ມ ${district}`);
    if (province) localityChunks.push(`ຂ ${province}`);
    return {
      addressDetail: addressLine,
      localityLine:
        localityChunks.length > 0 ? localityChunks.join(", ") : null,
    };
  }

  const parsed = parseCustomerAddressForBackfill(customer.address);
  if (parsed.parsed) {
    const localityChunks: string[] = [];
    if (parsed.district) localityChunks.push(`ມ ${parsed.district}`);
    if (parsed.province) localityChunks.push(`ຂ ${parsed.province}`);
    return {
      addressDetail: cleanPart(parsed.addressLine),
      localityLine:
        localityChunks.length > 0 ? localityChunks.join(", ") : null,
    };
  }

  return {
    addressDetail: cleanPart(customer.address),
    localityLine: null,
  };
}

function composeLabelAddressText(parts: {
  addressDetail: string | null;
  localityLine: string | null;
}): string {
  const lines = [parts.addressDetail, parts.localityLine].filter(Boolean);
  return lines.length > 0 ? lines.join("\n") : "—";
}

function formatCarrierInfo(
  order: Pick<
    OrderDetailDTO,
    "deliveryMethod" | "shippingCarrier" | "shippingBranch"
  >,
): Pick<ShippingLabelViewModel, "carrierLine" | "carrierName" | "branchName"> {
  if (order.deliveryMethod === "pickup") {
    return {
      carrierName: "ຮັບເອງທີ່ຮ້ານ",
      branchName: null,
      carrierLine: "ຮັບເອງທີ່ຮ້ານ",
    };
  }
  if (order.deliveryMethod === "self_delivery") {
    return {
      carrierName: "ຮ້ານໄປສົ່ງ",
      branchName: null,
      carrierLine: "ຮ້ານໄປສົ່ງ",
    };
  }

  const carrier = order.shippingCarrier?.trim();
  const carrierName = carrier || "ຂົນສົ່ງ";
  const branch = order.shippingBranch?.trim() || null;

  return {
    carrierName,
    branchName: branch,
    carrierLine: branch ? `${carrierName} — ສາຂາ ${branch}` : carrierName,
  };
}

/** รวมชื่อหมวดจากรายการสินค้า — แยก " / " แล้วไม่ซ้ำ */
function uniqueCategoryLabel(
  saleItems: Array<{ categoryName?: string | null; productName: string }>,
): string {
  const names = new Set<string>();
  for (const item of saleItems) {
    const raw = item.categoryName?.trim();
    if (!raw) continue;
    for (const part of raw.split(/\s*\/\s*/)) {
      const name = part.trim();
      if (name) names.add(name);
    }
  }
  if (names.size > 0) return [...names].join("/");
  return saleItems.map((item) => item.productName).join(", ") || "—";
}

export function buildShippingLabelViewModel(
  order: OrderDetailDTO,
  shopSettings: Pick<ShopSettingsDTO, "shopPhone">,
): ShippingLabelViewModel {
  const saleItems = order.items.filter((item) => !item.isBonus);
  const categoryLabel = uniqueCategoryLabel(saleItems);
  const carrier = formatCarrierInfo(order);
  const addressParts = formatShippingLabelAddressParts(order.customer);

  return {
    senderPhone: shopSettings.shopPhone.trim() || "—",
    recipientName: order.customer.name.trim() || "—",
    recipientPhone: order.customer.phone?.trim() || "—",
    categoryLabel,
    ...carrier,
    ...addressParts,
    address: composeLabelAddressText(addressParts),
    isCod: order.paymentType === "cod",
    codAmount: order.total,
    collectShippingAtDestination:
      order.paymentType === "cod" && Number(order.shippingFee) > 0,
  };
}

export function formatLabelAmount(amount: string | number): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("lo-LA", { maximumFractionDigits: 0 }).format(n);
}
